"use client";

import React, { useState } from "react";
import { useRouter } from "next/navigation";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Loader2, CheckCircle2 } from "lucide-react";
import { registerProfessional } from "@/actions/registration";
import { AccountConnectedCard } from "../account-connected-card";
import { cn } from "@/lib/utils";

interface AuthenticatedUser {
  id: string;
  email: string;
  name: string | null;
}

interface ProfessionalRegistrationFormProps {
  authenticatedUser?: AuthenticatedUser | null;
}

const professionalFormSchema = z.object({
  email: z.string().email("Invalid email address"),
  fullName: z.string().min(2, "Name must be at least 2 characters"),
  phone: z.string().optional(),
  organisation: z.string().min(2, "Organisation name is required"),
  jobRole: z.string().min(2, "Role/Position is required"),
  referralName: z.string().optional(),
});

type ProfessionalFormData = z.infer<typeof professionalFormSchema>;

export function ProfessionalRegistrationForm({
  authenticatedUser,
}: ProfessionalRegistrationFormProps) {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const form = useForm<ProfessionalFormData>({
    resolver: zodResolver(professionalFormSchema),
    defaultValues: {
      email: authenticatedUser?.email || "",
      fullName: authenticatedUser?.name || "",
      organisation: "",
      jobRole: "",
      referralName: "",
    },
  });

  const onSubmit = async (data: ProfessionalFormData) => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const submitData = {
        email: data.email || "",
        fullName: data.fullName || "",
        phone: data.phone,
        organisation: data.organisation || "",
        jobRole: data.jobRole || "",
        referralName: data.referralName,
      };

      const result = await registerProfessional(submitData, authenticatedUser?.id);

      if (result.success) {
        if (authenticatedUser) {
          router.push("/success?type=professional&verified=true");
        } else {
          router.push(
            `/verification-pending?email=${encodeURIComponent(submitData.email)}&type=professional`,
          );
        }
      } else {
        setErrorMessage(
          result.message ||
            "Registration failed. Please check your details and try again.",
        );
      }
    } catch (error) {
      console.error("Registration error:", error);
      setErrorMessage(
        "An unexpected error occurred. Please try again later.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      <AccountConnectedCard user={authenticatedUser} />
      {errorMessage && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{errorMessage}</p>
        </div>
      )}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Email Field */}
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2">
                  Email Address
                  {authenticatedUser && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                      <CheckCircle2 className="h-3 w-3" />
                      Verified
                    </span>
                  )}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="email"
                    placeholder="you@company.com"
                    disabled={!!authenticatedUser}
                    className={cn(
                      authenticatedUser && "bg-gray-50 cursor-not-allowed",
                    )}
                  />
                </FormControl>
                {authenticatedUser && (
                  <p className="text-xs text-green-600 mt-1">
                    Via Edukate Africa account
                  </p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Full Name Field */}
          <FormField
            control={form.control}
            name="fullName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Full Name</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Your name"
                    disabled={isLoading}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Organisation Field */}
          <FormField
            control={form.control}
            name="organisation"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Organisation</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Company or organization name"
                    disabled={isLoading}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Role/Position Field */}
          <FormField
            control={form.control}
            name="jobRole"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Role/Position</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="e.g., CEO, Investor, Consultant"
                    disabled={isLoading}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Phone Field */}
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone Number (Optional)</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="tel"
                    placeholder="+1 (555) 000-0000"
                    disabled={isLoading}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Referral Name Field */}
          <FormField
            control={form.control}
            name="referralName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Referral Name (Optional)</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Who referred you to this event?"
                    disabled={isLoading}
                  />
                </FormControl>
                <FormDescription className="text-xs">
                  If someone referred you to this event, please mention their name
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isLoading}
            className="w-full bg-blue-600 hover:bg-blue-700"
            size="lg"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Registering...
              </>
            ) : (
              "Complete Registration"
            )}
          </Button>

          <p className="text-center text-xs text-gray-600">
            {authenticatedUser
              ? "Your registration will be instantly verified"
              : "We'll send you a verification email to confirm your registration"}
          </p>
        </form>
      </Form>
    </div>
  );
}
